import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Home, Gamepad2, Wallet, Trophy, Bell, Menu, X, HelpCircle, ChevronLeft, Headphones } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import { collection, onSnapshot, query, where, limit } from 'firebase/firestore';
import { db } from '../config/firebase';
import AvatarDisplay from './AvatarDisplay';

const navLinks = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/tournaments', label: 'Tournaments', icon: Gamepad2 },
    { path: '/wallet', label: 'Wallet', icon: Wallet },
    { path: '/my-tournaments', label: 'My Matches', icon: Trophy },
    { path: '/leaderboard', label: 'Leaderboard', icon: Trophy, customImage: '/icons/podium.png' },
    { path: '/support', label: 'Support', icon: HelpCircle, customImage: '/icons/support.png' },
];

/** Root-level paths where the avatar shows instead of a back button */
const ROOT_PATHS = ['/', '/tournaments', '/wallet', '/my-tournaments', '/support', '/leaderboard'];

/** Bottom nav uses a trimmed 5-tab set on mobile */
const mobileBottomLinks = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/tournaments', label: 'Play', icon: Trophy },
    { path: '/wallet', label: 'Wallet', icon: Wallet },
    { path: '/my-tournaments', label: 'Matches', icon: Gamepad2 },
    { path: '/support', label: 'Support', icon: Headphones },
];

interface NavbarProps {
    isLoggedIn: boolean;
}

export default function Navbar({ isLoggedIn }: NavbarProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, loginWithGoogle } = useAuth();
    const { showAlert } = useModal();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);

    const handleGoogleSignIn = async () => {
        try {
            await loginWithGoogle();
        } catch (err: any) {
            console.error('Google sign-in error:', err.code, err.message, err);
            if (err.code !== 'auth/popup-closed-by-user') {
                showAlert('Google sign-in failed. Please try again.', 'Error');
            }
        }
    };

    const isRootPage = ROOT_PATHS.includes(location.pathname);

    useEffect(() => {
        if (!user?.uid) return;

        const q = query(
            collection(db, `users/${user.uid}/notifications`),
            where('read', '==', false),
            limit(100)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setUnreadCount(snapshot.size);
        });

        return () => unsubscribe();
    }, [user?.uid]);

    // Avatar is rendered by AvatarDisplay component which handles URLs, base64, emoji, and fallback initials

    return (
        <>
            {/* ─── Mobile Top Bar (≤768px, hidden on desktop via CSS) ─── */}
            <div className="mobile-top-bar">
                {/* Left: Avatar or Back button */}
                <div className="mobile-top-left">
                    {isRootPage ? (
                        isLoggedIn ? (
                            <NavLink to="/profile" className="mobile-avatar-link">
                                <AvatarDisplay
                                    avatar={user?.photoURL}
                                    username={user?.displayName || user?.email || 'Player'}
                                    size={34}
                                    className="avatar"
                                />
                            </NavLink>
                        ) : (
                            <button onClick={handleGoogleSignIn} className="mobile-avatar-link" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                                <img src="/log-in.png" alt="Sign In" style={{ width: 34, height: 34, objectFit: 'contain', borderRadius: 8 }} />
                            </button>
                        )
                    ) : (
                        <button
                            className="mobile-back-btn"
                            onClick={() => navigate(-1)}
                            title="Go back"
                        >
                            <ChevronLeft size={20} />
                        </button>
                    )}
                </div>

                {/* Center: Brand */}
                <NavLink to="/" className="mobile-brand">
                    <div className="mobile-brand-icon">🎮</div>
                    <span className="mobile-brand-text">TournaHub</span>
                </NavLink>

                {/* Right: Trophy + Bell */}
                <div className="mobile-top-actions">
                    <NavLink to="/leaderboard">
                        <button className="mobile-action-btn" title="Leaderboard">
                            <img src="/icons/podium.png" alt="Leaderboard" style={{ width: 18, height: 18, objectFit: 'contain' }} />
                        </button>
                    </NavLink>
                    <NavLink to="/notifications">
                        <button className="mobile-notif-btn" title="Notifications">
                            <Bell size={16} />
                            {unreadCount > 0 && (
                                <span className="mobile-notif-badge">
                                    {unreadCount > 99 ? '99+' : unreadCount}
                                </span>
                            )}
                        </button>
                    </NavLink>
                </div>
            </div>

            {/* ─── Desktop Navbar (hidden ≤768px via CSS) ─── */}
            <nav className="navbar">
                <NavLink to="/" className="navbar-brand">
                    <div className="brand-icon">🎮</div>
                    <span className="brand-text">TournaHub</span>
                </NavLink>

                <div className="navbar-links">
                    {navLinks.map(l => (
                        <NavLink
                            key={l.path}
                            to={l.path}
                            className={location.pathname === l.path ? 'active' : ''}
                            end={l.path === '/'}
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </div>

                <div className="navbar-actions">
                    {isLoggedIn ? (
                        <>
                            <NavLink to="/notifications">
                                <button className="btn-icon" title="Notifications" style={{ position: 'relative' }}>
                                    <Bell size={18} />
                                    {unreadCount > 0 && (
                                        <span style={{
                                            position: 'absolute',
                                            top: 2,
                                            right: 2,
                                            backgroundColor: '#ef4444',
                                            color: '#ffffff',
                                            fontSize: '0.65rem',
                                            fontWeight: 800,
                                            minWidth: 16,
                                            height: 16,
                                            borderRadius: 8,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: '0 4px',
                                            lineHeight: 1
                                        }}>
                                            {unreadCount > 99 ? '99+' : unreadCount}
                                        </span>
                                    )}
                                </button>
                            </NavLink>
                            <NavLink to="/profile" className="user-pill">
                                <AvatarDisplay avatar={user?.photoURL} username={user?.displayName || user?.email || 'Player'} size={28} className="avatar" />
                                <span className="balance" style={{ fontSize: '0.8rem' }}>{user?.displayName || 'Player'}</span>
                            </NavLink>
                        </>
                    ) : (
                        <button className="navbar-btn primary" onClick={handleGoogleSignIn}>🔑 Sign In with Google</button>
                    )}
                    <button className="btn-icon" onClick={() => setMobileOpen(!mobileOpen)} style={{ display: 'none' }}>
                        {mobileOpen ? <X size={18} /> : <Menu size={18} />}
                    </button>
                </div>
            </nav>

            {/* Bottom Nav (mobile) */}
            <div className="bottom-nav">
                <div className="bottom-nav-inner bottom-nav-desktop">
                    {/* On ≤768px, use the trimmed 5-tab set; on 769+ use full 6-tab set */}
                    {navLinks.slice(0, 6).map(l => (
                        <NavLink
                            key={l.path}
                            to={l.path}
                            className={`bottom-nav-link-desktop ${location.pathname === l.path ? 'active' : ''}`}
                            end={l.path === '/'}
                        >
                            {l.customImage ? (
                                <img src={l.customImage} alt={l.label} style={{ width: 22, height: 22, objectFit: 'contain' }} />
                            ) : (
                                <l.icon size={20} />
                            )}
                            <span>{l.label}</span>
                            <span className="nav-icon-dot" />
                        </NavLink>
                    ))}
                </div>

                {/* Mobile-only 5-tab bottom nav (replaces the above via CSS at ≤768px) */}
                <div className="bottom-nav-inner bottom-nav-mobile">
                    {mobileBottomLinks.map(l => (
                        <NavLink
                            key={l.path}
                            to={l.path}
                            className={location.pathname === l.path ? 'active' : ''}
                            end={l.path === '/'}
                        >
                            <l.icon size={22} />
                            <span>{l.label}</span>
                            <span className="nav-icon-dot" />
                        </NavLink>
                    ))}
                </div>
            </div>
        </>
    );
}
